import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bot, History, Plus, Search } from 'lucide-react';
import { ErrorState, Skeleton } from '@shared/components';
import { cn } from '@core/utils/cn';
import { HOME_ROUTE } from '@config/routes';
import { useAssistantStore } from '@stores/assistant.store';
import { useHistoryTree } from '../hooks/useHistoryTree';
import { shortcutHint } from '../utils/platform';
import { HistoryGroup } from './HistoryGroup';

export interface HistorySidebarProps {
  /** Called after any row is followed — the mobile drawer uses it to close. */
  readonly onNavigate?: () => void;
}

/** Rows are 44px where a thumb has to hit them and 36px on the desktop rail,
 *  where the list is long and every extra pixel costs a project off the screen. */
const ROW =
  'flex min-h-11 items-center gap-2.5 rounded-lg px-2.5 text-sm outline-none ' +
  'transition-colors dda-focus lg:min-h-9';

/**
 * The history rail: what the caller has, newest first.
 *
 * This is the navigation now — it replaces the two cards that used to be the only
 * way around the app. Shaped like the sidebars people already know: one primary
 * action at the top, a flat block of destinations under it, then the history in
 * recency bands. Deliberately text-first — no counts, no dates, no badges on the
 * rows. A rail is scanned, not read, and every chip on a row is one more thing to
 * skip past on the way to the title.
 */
export function HistorySidebar({ onNavigate }: HistorySidebarProps): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const requestFocus = useAssistantStore((state) => state.requestFocus);
  const { groups, isPending, isError, refetch } = useHistoryTree();

  const handleSearch = (): void => {
    // The composer only exists on home, so searching from anywhere else means
    // going there first and asking it for focus once it has mounted.
    void navigate(HOME_ROUTE);
    requestFocus();
    onNavigate?.();
  };

  return (
    <nav aria-label={t('assistant.railLabel')} className="flex h-full flex-col p-2.5">
      {/* The one action that makes something new, given the weight to match. */}
      <Link
        to="/projects/new"
        onClick={onNavigate}
        className={cn(
          ROW,
          'border border-accent/25 bg-accent/[0.07] font-medium',
          'hover:border-accent/50 hover:bg-accent/[0.12]',
        )}
      >
        <Plus size={16} aria-hidden="true" className="shrink-0 text-accent" />
        <span className="truncate">{t('assistant.newProject')}</span>
      </Link>

      <div className="mt-1.5 space-y-0.5">
        <button
          type="button"
          onClick={handleSearch}
          className={cn(ROW, 'w-full text-muted hover:bg-bg/70 hover:text-text')}
        >
          <Search size={16} aria-hidden="true" className="shrink-0" />
          <span className="truncate">{t('assistant.search')}</span>
          {/* Cosmetic: the real binding lives in the shell, which owns the keymap. */}
          <kbd className="ms-auto hidden shrink-0 dda-readout rounded border border-line px-1.5 py-0.5 lg:inline">
            {shortcutHint()}
          </kbd>
        </button>

        {/* The full history screen is still a place worth having — the rail shows
            the caller's projects, that page shows what each one covered. */}
        <Link
          to="/sessions"
          onClick={onNavigate}
          aria-current={pathname === '/sessions' ? 'page' : undefined}
          className={cn(
            ROW,
            'text-muted hover:bg-bg/70 hover:text-text',
            pathname === '/sessions' && 'bg-bg text-text',
          )}
        >
          <History size={16} aria-hidden="true" className="shrink-0" />
          <span className="truncate">{t('home.historyTitle')}</span>
        </Link>

        {/* Beside the history rather than behind an account menu: handing an assistant a
            token is something a person does once and then forgets where. */}
        <Link
          to="/mcp"
          onClick={onNavigate}
          aria-current={pathname === '/mcp' ? 'page' : undefined}
          className={cn(
            ROW,
            'text-muted hover:bg-bg/70 hover:text-text',
            pathname === '/mcp' && 'bg-bg text-text',
          )}
        >
          <Bot size={16} aria-hidden="true" className="shrink-0" />
          <span className="truncate">{t('mcp.title')}</span>
        </Link>
      </div>

      <div className="mx-1 my-2.5 border-t border-line" aria-hidden="true" />

      <div className="dda-scroll min-h-0 flex-1 overflow-y-auto">
        {isPending ? (
          <div className="space-y-2 px-1 pt-1" data-testid="rail-loading">
            {[0, 1, 2, 3, 4].map((row) => (
              <Skeleton key={row} className="h-7" />
            ))}
          </div>
        ) : isError ? (
          <ErrorState onRetry={refetch} />
        ) : groups.length === 0 ? (
          <p className="flex flex-col items-center gap-2 px-3 py-8 text-center text-xs text-muted">
            <History size={20} aria-hidden="true" />
            {t('assistant.railEmpty')}
          </p>
        ) : (
          <div data-testid="rail-history">
            {groups.map((group) => (
              <HistoryGroup
                key={group.bucket}
                bucket={group.bucket}
                entries={group.items}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        )}
      </div>
    </nav>
  );
}
